import React, { useEffect, useState } from "react";
import { FormStep } from "../InteractiveForm";
import { ArcherContainer, ArcherElement } from "react-archer";
import {
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
} from "@heroicons/react/24/outline";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/16/solid";
import { Link, useBlocker, useLoaderData, useNavigate } from "react-router-dom";
import useUndoRedo from "../../custom_hooks/UndoRedo";
import ButtonLoadingOnBusy from "../Library/LoadingButton";
import {
  useCreateAlgorithm,
  useDeleteAlgorithm,
  useEditAlgorithm,
} from "./queries";
import { IFormAlgorithm, IFormAlgorithmDB } from "./interfaces";
import { enqueueSnackbar } from "notistack";

function AnswerComponent(props: {
  formData:
    | {
        answer: string;
        form: FormStep;
      }
    | {
        answer: string;
        recommendation: string;
      };
  onChange: (
    value:
      | {
          answer: string;
          form: FormStep;
        }
      | {
          answer: string;
          recommendation: string;
        }
  ) => void;
  deleteAnswer: () => void;
  moveUp: undefined | (() => void);
  moveDown: undefined | (() => void);
  id: string[];
}) {
  const { formData, onChange, deleteAnswer, moveUp, moveDown, id } = props;
  const isIntermediateAnswer = "form" in formData;
  const [isCollapsed, setIsCollapsed] = useState(false);

  const onPropertyChange = (name: string) => {
    return (value: string | FormStep) => {
      const newFormData = { ...formData, [name]: value };
      onChange(newFormData);
    };
  };
  const switchAnswerType = () => {
    let newFormData;
    if (isIntermediateAnswer) {
      newFormData = {
        answer: formData.answer,
        recommendation: "New Recommendation",
      };
    } else {
      newFormData = {
        answer: formData.answer,
        form: {
          question: "New Question",
          answers: [
            {
              answer: "New Final Answer",
              recommendation: "New Recommendation",
            },
          ],
        },
      };
    }
    onChange(newFormData);
  };
  const switchToText = isIntermediateAnswer ? "recommendation" : "question";
  const borderColor = isIntermediateAnswer
    ? "border-slate-500"
    : "border-emerald-600";
  const padding = isIntermediateAnswer ? "p-1" : "p-3";
  const switchButton = (
    <button
      className="btn btn-ghost p-0 text-xs h-min min-h-min"
      onClick={switchAnswerType}
    >
      Switch to {switchToText}
    </button>
  );
  const collapseButton = (
    <button
      className="btn btn-ghost p-0 text-xs h-min min-h-min collapse-arrow"
      onClick={() => setIsCollapsed((prev) => !prev)}
    >
      {isCollapsed ? (
        <>
          Expand <ChevronDownIcon className="h-4 w-4" />
        </>
      ) : (
        <>
          Collapse <ChevronUpIcon className="h-4 w-4" />
        </>
      )}
    </button>
  );
  return (
    <ArcherElement id={id.join("_")}>
      <div
        className={`border-solid border-2 rounded-md ${borderColor} ${padding} pt-1 m-1 flex flex-col items-center relative`}
      >
        <div className="mb-2">
          <FormStringInputComponent
            title="Answer"
            formData={formData.answer}
            onChange={onPropertyChange("answer")}
            id={[...id, "answer"]}
            switchButton={collapseButton}
          />
        </div>
        {isCollapsed ? (
          <span className="text-xs">
            {"<"}Collapsed{">"}
          </span>
        ) : isIntermediateAnswer ? (
          <FormStepComponent
            formData={formData.form}
            onChange={onPropertyChange("form")}
            id={[...id, "form"]}
            switchButton={switchButton}
          />
        ) : (
          <div>
            <FormStringInputComponent
              title="Recommendation"
              formData={formData.recommendation}
              onChange={onPropertyChange("recommendation")}
              id={[...id, "recommendation"]}
              switchButton={switchButton}
            />
          </div>
        )}
        <div>
          {moveDown && (
            <button className="btn my-2 mr-1" onClick={() => moveDown()}>
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
          )}
          <button
            className={`btn my-2 ${moveDown || moveUp ? "" : "btn-disabled"}`}
            onClick={() => deleteAnswer()}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
          {moveUp && (
            <button className="btn my-2 ml-1" onClick={() => moveUp()}>
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </ArcherElement>
  );
}

interface FormStringInputProps {
  formData: string;
  title: string;
  onChange: (value: string) => void;
  id: string[];
  classNames?: string[];
  switchButton?: JSX.Element;
}

type FormStringInputRef = HTMLDivElement;

const FormStringInputComponent = React.forwardRef<
  FormStringInputRef,
  FormStringInputProps
>((props, ref) => {
  const { formData, title, onChange, classNames, switchButton } = props;
  const [inputState, setInputState] = useState(formData);
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = () => {
    if (inputState !== formData) {
      onChange(inputState);
    }
  };
  const handleBlur = () => {
    setIsEditing(false);
    handleSave();
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setInputState(formData);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault(); // Prevent newline on Ctrl + Enter
      handleBlur();
    } else if (event.key === "Escape") {
      handleCancelEdit();
    }
  };

  useEffect(() => {
    if (!isEditing && formData !== inputState) {
      // Case where text modified externally (eg. undo, redo)
      setInputState(formData);
    }
  }, [isEditing, formData, inputState]);

  return (
    <>
      <div className="label pb-px self-start">
        <span className="label-text">{title}</span>
        {switchButton && <span className="label-text-alt">{switchButton}</span>}
      </div>
      {isEditing ? (
        <textarea
          autoFocus
          value={inputState}
          className={
            "textarea textarea-bordered w-full max-w-xs min-w-72 text-sm" +
            (classNames ? " " + classNames.join(" ") : "")
          }
          onFocus={(e) =>
            e.currentTarget.setSelectionRange(
              e.currentTarget.value.length,
              e.currentTarget.value.length
            )
          } // Set cursor to the end of the text
          onChange={(e) => setInputState(e.target.value)}
          onKeyDown={handleKeyPress}
          onBlur={handleBlur}
        />
      ) : (
        <>
          <div
            ref={ref}
            className={
              "input input-disabled max-w-72 min-w-72 text-sm cursor-default bg-inherit flex items-center pr-0 text-inherit" +
              (inputState.length === 0 ? " border-error" : "") +
              (classNames ? " " + classNames.join(" ") : "")
            }
          >
            <div className="truncate grow">{inputState}</div>
            <button
              onClick={() => setIsEditing(true)}
              className="btn btn-ghost"
            >
              <PencilSquareIcon className="w-3 h-3" />
            </button>
          </div>
          {inputState.length === 0 && (
            <div className="label">
              <span className="label-text-alt text-error">
                Input cannot be blank
              </span>
            </div>
          )}
        </>
      )}
    </>
  );
});

interface FormStepProps {
  formData: FormStep;
  onChange: (value: FormStep) => void;
  id: string[];
  switchButton?: JSX.Element;
}
const FormStepComponent = React.forwardRef<FormStringInputRef, FormStepProps>(
  (props, ref) => {
    const { formData, onChange, id, switchButton } = props;
    const joinedId = id.join("_");
    const onPropertyChange = (name: string) => {
      return (
        value:
          | string
          | Array<
              | { answer: string; form: FormStep }
              | { answer: string; recommendation: string }
            >
      ) => {
        const newFormData = { ...formData, [name]: value };
        onChange(newFormData);
      };
    };

    const onChangeForIndex = (index: number) => {
      return (
        value:
          | { answer: string; form: FormStep }
          | { answer: string; recommendation: string }
      ) => {
        const arrayData = formData.answers;
        const newFormData = arrayData.map((item, i: number) => {
          const jsonValue = value;
          return index === i ? jsonValue : item;
        });
        onPropertyChange("answers")(newFormData);
      };
    };
    const handleAddFinalAnswer = () => {
      const nextAnswers = [
        ...formData.answers,
        {
          answer: "New Final Answer",
          recommendation: "New Recommendation",
        },
      ];
      onPropertyChange("answers")(nextAnswers);
    };
    const deleteAnswer = (answerIndex: number) => () => {
      if (formData.answers.length === 1) {
        alert("Must have at least 1 answer.");
        return;
      }
      const nextAnswers = formData.answers.filter(
        (_value, index) => index !== answerIndex
      );
      onPropertyChange("answers")(nextAnswers);
    };
    const swapArrayElements = (index1: number, index2: number) => {
      const nextAnswers = [...formData.answers];
      [nextAnswers[index1], nextAnswers[index2]] = [
        nextAnswers[index2],
        nextAnswers[index1],
      ];
      onPropertyChange("answers")(nextAnswers);
    };
    const moveUp = (index: number) => swapArrayElements(index, index + 1);
    const moveDown = (index: number) => swapArrayElements(index, index - 1);

    return (
      <div className="flex flex-col items-center">
        <ArcherElement
          id={joinedId}
          relations={Array.from(
            { length: formData.answers.length },
            (_, i) => ({
              targetId: `${joinedId}_answer-${i}`,
              targetAnchor: "top",
              sourceAnchor: "bottom",
            })
          )}
        >
          <div className="mb-8">
            <FormStringInputComponent
              formData={formData.question}
              title="Question"
              onChange={onPropertyChange("question")}
              id={[...id, "question"]}
              ref={ref}
              switchButton={switchButton}
            />
          </div>
        </ArcherElement>
        <div className="flex justify-evenly">
          {/* items-start"> */}
          {formData.answers.map((answer, index) => {
            const currentMoveDown =
              index === 0 ? undefined : () => moveDown(index);
            const currentMoveUp =
              index === formData.answers.length - 1
                ? undefined
                : () => moveUp(index);
            return (
              <AnswerComponent
                key={index}
                formData={answer}
                onChange={onChangeForIndex(index)}
                deleteAnswer={deleteAnswer(index)}
                moveDown={currentMoveDown}
                moveUp={currentMoveUp}
                id={[...id, `answer-${index}`]}
              />
            );
          })}
        </div>
        <div>
          <button className="btn mx-1 mb-1" onClick={handleAddFinalAnswer}>
            {" "}
            Add answer
          </button>
        </div>
      </div>
    );
  }
);

const templateForm = {
  question: "Question 1",
  answers: [
    { answer: "Answer 1", recommendation: "Recommendation 1" },
    {
      answer: "Answer 2",
      form: {
        question: "Followup Question",
        answers: [
          { answer: "Sub-Answer", recommendation: "Sub-Recommendation" },
        ],
      },
    },
  ],
};

// const ResponseModal = React.forwardRef<HTMLDialogElement, { content?: string }>(
//   (props, ref) => {
//     return (
//       <dialog ref={ref} className="modal">
//         <div className="modal-box w-11/12 max-w-5xl">
//           <h3 className="font-bold text-lg">Response</h3>
//           <p
//             className="py-4"
//             style={{
//               textAlign: "left",
//               fontFamily: "monospace,serif",
//               fontSize: "0.75em",
//               whiteSpace: "pre-line",
//             }}
//           >
//             {/* Press ESC key or click the button below to close */}
//             {props.content ? props.content : "Error"}
//           </p>
//           <div className="modal-action">
//             {/* <CustomTooltip content="Copied!" trigger="click">
//               <button
//                 className="btn"
//                 onClick={() => copyToClipboard(response.EN || "")}
//               >
//                 Copy
//               </button>
//             </CustomTooltip> */}
//             <form method="dialog">
//               {/* if there is a button in form, it will close the modal */}
//               <button className="btn">Close</button>
//             </form>
//           </div>
//         </div>
//       </dialog>
//     );
//   }
// );
const DeleteFormModal = React.forwardRef<
  HTMLDialogElement,
  { content?: string; handleDelete: () => void; isDeleting: boolean }
>((props, ref) => {
  const { content, handleDelete, isDeleting } = props;
  const [confirmQuestion, setConfirmQuestion] = useState("");
  return (
    <dialog ref={ref} className="modal">
      <div className="modal-box w-11/12 max-w-5xl">
        <h3 className="font-bold text-lg">DANGER: DELETING</h3>
        <p
          className="py-4"
          style={{
            textAlign: "left",
            fontFamily: "monospace,serif",
            fontSize: "0.75em",
            whiteSpace: "pre-line",
          }}
        >
          {/* Press ESC key or click the button below to close */}
          Are you sure you want to delete this form? Type the first question '
          <span className="font-bold">{content}</span>' in the box below to
          confirm:
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (confirmQuestion === content) {
              handleDelete();
            }
          }}
        >
          <input
            type="text"
            value={confirmQuestion}
            className={
              "input input-bordered input-warning w-full max-w-2xl text-sm"
            }
            onChange={(e) => setConfirmQuestion(e.target.value)}
          />
        </form>
        <div className="modal-action">
          <ButtonLoadingOnBusy
            className="btn btn-error"
            disabled={confirmQuestion !== content}
            onClick={handleDelete}
            busy={isDeleting}
          >
            Delete{" "}
          </ButtonLoadingOnBusy>
          {/* <CustomTooltip content="Copied!" trigger="click">
              <button
                className="btn"
                onClick={() => copyToClipboard(response.EN || "")}
              >
                Copy
              </button>
            </CustomTooltip> */}
          <form method="dialog">
            {/* if there is a button in form, it will close the modal */}
            <button className="btn" disabled={isDeleting}>
              Close
            </button>
          </form>
        </div>
      </div>
    </dialog>
  );
});

// const BlockedModal: React.FC<{
//   handleProceed: () => void;
//   handleReset: () => void;
// }> = (props) => {
//   return (
//     <dialog className="modal modal-open">
//       <div className="modal-box w-11/12 max-w-5xl">
//         <h3 className="font-bold text-lg">Are you sure you want to leave?</h3>
//         <p
//           className="py-4"
//           style={{
//             textAlign: "left",
//             fontFamily: "monospace,serif",
//             fontSize: "0.75em",
//             whiteSpace: "pre-line",
//           }}
//         >
//           {/* Press ESC key or click the button below to close */}
//           Information you've entered may not be saved.
//         </p>
//         <div className="modal-action">
//           <button className="btn btn-info" onClick={props.handleProceed}>
//             Leave page
//           </button>
//           <button className="btn" onClick={props.handleReset}>
//             Stay on page
//           </button>
//         </div>
//       </div>
//     </dialog>
//   );
// };

const FormTitle: React.FC<{
  title: string;
  onChange: (newTitle: string) => void;
}> = ({ title, onChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputState, setInputState] = useState(title);
  const handleSave = () => {
    setIsEditing(false);
    if (inputState !== title) {
      onChange(inputState);
    }
  };
  const handleCancelEdit = () => {
    setIsEditing(false);
    setInputState(title);
  };
  useEffect(() => {
    if (!isEditing && title !== inputState) {
      // Case where text modified externally (eg. undo, redo)
      setInputState(title);
    }
  }, [isEditing, title, inputState]);
  return (
    <div className="flex justify-center items-center gap-4 mb-4">
      {isEditing ? (
        <>
          <input
            type="text"
            value={inputState}
            className={"input input-bordered w-full max-w-2xl text-3xl"}
            onChange={(e) => setInputState(e.target.value)}
          />
          <button className="btn btn-ghost" onClick={handleSave}>
            <CheckIcon className="w-4 h-4" />
          </button>
          <button className="btn btn-ghost" onClick={handleCancelEdit}>
            <XMarkIcon className="w-4 h-4" />
          </button>
        </>
      ) : (
        <>
          <span className="truncate text-3xl font-bold">{inputState}</span>
          <button className="btn btn-ghost" onClick={() => setIsEditing(true)}>
            <PencilSquareIcon className="w-4 h-4" />
          </button>
        </>
      )}
    </div>
  );
};

const fetchStoredChanges = (key: string) => {
  const storedChanges = sessionStorage.getItem("storedChanges");
  if (storedChanges === null) {
    sessionStorage.setItem("storedChanges", "{}");
  } else {
    const parsedChanges = JSON.parse(storedChanges) as {
      [key: string]: {
        title: string;
        form: FormStep;
      };
    };
    if (key in parsedChanges) {
      const fetchedChanges = parsedChanges[key];
      delete parsedChanges[key];
      sessionStorage.setItem("storedChanges", JSON.stringify(parsedChanges));
      return fetchedChanges;
    }
  }
  return null;
};

const storeChanges = (
  key: string,
  form: {
    title: string;
    form: FormStep;
  }
) => {
  const storedChanges = sessionStorage.getItem("storedChanges") || "{}";
  const parsedChanges = JSON.parse(storedChanges) as {
    [key: string]: {
      title: string;
      form: FormStep;
    };
  };
  parsedChanges[key] = form;
  sessionStorage.setItem("storedChanges", JSON.stringify(parsedChanges));
};

const AlgorithmBuilder: React.FC = () => {
  const { data } = useLoaderData() as {
    data: IFormAlgorithmDB | null;
  };
  const isCreate = data === null;
  const initialForm = isCreate ? templateForm : data.algorithm;
  const navigate = useNavigate();
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [isPublic, setIsPublic] = useState(isCreate ? false : data.is_public);
  const {
    state: formAndTitleState,
    undo,
    redo,
    updatePresent: setFormAndTitle,
    hasUndo,
    hasRedo,
    reset: resetFormAndTitleState,
  } = useUndoRedo<{ form: FormStep; title: string }>({
    title: isCreate ? "Blank Title" : data.title,
    form: initialForm,
  });
  const [lastSavedFormAndTitle, setLastSavedFormAndTitle] = useState<{
    title: string;
    form: FormStep;
  }>({ title: formAndTitleState.title, form: initialForm });

  const isEdited =
    lastSavedFormAndTitle.title !== formAndTitleState.title ||
    JSON.stringify(lastSavedFormAndTitle.form) !==
      JSON.stringify(formAndTitleState.form);
  const reset = () => {
    if (isEdited) {
      resetFormAndTitleState();
    }
  };

  const setFormStepEntry = (formData: FormStep) => {
    setFormAndTitle({ title: formAndTitleState.title, form: formData });
  };
  const setFormTitle = (title: string) => {
    setFormAndTitle({ title: title, form: formAndTitleState.form });
  };
  const onChange = (formData: FormStep) => {
    setFormStepEntry(formData);
  };

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      navigateTo === null &&
      isEdited &&
      currentLocation.pathname !== nextLocation.pathname
  );
  useEffect(() => {
    if (!isEdited) return;
    function handleOnBeforeUnload(event: BeforeUnloadEvent) {
      const key = data ? data.uuid : "new-form";
      storeChanges(key, formAndTitleState);
      return (event.returnValue = "");
    }
    window.addEventListener("beforeunload", handleOnBeforeUnload, {
      capture: true,
    });
    return () => {
      window.removeEventListener("beforeunload", handleOnBeforeUnload, {
        capture: true,
      });
    };
  }, [isEdited, data, formAndTitleState]);

  useEffect(() => {
    const key = data ? data.uuid : "new-form";
    const fetchedChanges = fetchStoredChanges(key);
    if (fetchedChanges !== null) {
      setFormAndTitle(fetchedChanges);
      enqueueSnackbar("Fetched unsaved changes.", {
        variant: "info",
        preventDuplicate: true,
      });
    }
  }, [data, setFormAndTitle]);

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    const key = data ? data.uuid : "new-form";
    storeChanges(key, formAndTitleState);
    blocker.proceed();
  }, [blocker, isEdited, formAndTitleState, data]);

  useEffect(() => {
    if (navigateTo !== null) {
      navigate(navigateTo);
      setNavigateTo(null);
    }
  }, [navigateTo, navigate]);
  return (
    <>
      <FormTitle
        title={formAndTitleState.title}
        onChange={(newTitle: string) => setFormTitle(newTitle)}
      />
      <AlgorithmBuilderScreen
        onChange={onChange}
        formData={formAndTitleState.form}
        undo={undo}
        redo={redo}
        hasUndo={hasUndo}
        hasRedo={hasRedo}
      />
      <div className="flex justify-center items-center mb-4">
        {isCreate ? (
          <AlgorithmBuilderCreateButtons
            algorithmData={{
              algorithm: formAndTitleState.form,
              title: formAndTitleState.title,
            }}
            onSubmitSuccess={(
              uuid: string,
              submittedForm: FormStep,
              submittedTitle: string
            ) => {
              setLastSavedFormAndTitle({
                title: submittedTitle,
                form: submittedForm,
              });
              setNavigateTo(`/algorithm-builder/${uuid}`);
            }}
            reset={reset}
          />
        ) : (
          <AlgorithmBuilderEditButtons
            algorithmData={{
              algorithm: formAndTitleState.form,
              title: formAndTitleState.title,
            }}
            isEdited={isEdited}
            onSubmitSuccess={(
              submittedForm: FormStep,
              submittedTitle: string
            ) =>
              setLastSavedFormAndTitle({
                title: submittedTitle,
                form: submittedForm,
              })
            }
            onDeleteSuccess={() => {
              setNavigateTo("/algorithms/");
            }}
            algorithmId={data.uuid}
            isPublic={isPublic}
            onPublicChange={setIsPublic}
            reset={reset}
          />
        )}
      </div>
      {/* {blocker.state === "blocked" ? (
        <BlockedModal
          handleProceed={() => {
            const storedChanges =
              sessionStorage.getItem("storedChanges") || "{}";
            const parsedChanges = JSON.parse(storedChanges) as {
              [key: string]: {
                title: string;
                form: FormStep;
              };
            };
            const key = isCreate ? "new-form" : data.uuid;
            parsedChanges[key] = formAndTitleState;
            sessionStorage.setItem(
              "storedChanges",
              JSON.stringify(parsedChanges)
            );
            blocker.proceed();
          }}
          handleReset={blocker.reset}
        />
      ) : null} */}
    </>
  );
};

const AlgorithmBuilderEditButtons: React.FC<{
  algorithmData: IFormAlgorithm;
  isEdited: boolean;
  onSubmitSuccess: (submittedForm: FormStep, submittedTitle: string) => void;
  onDeleteSuccess: () => void;
  algorithmId: string;
  isPublic: boolean;
  onPublicChange: (isPublic: boolean) => void;
  reset: () => void;
}> = ({
  algorithmData,
  isEdited,
  onSubmitSuccess,
  onDeleteSuccess,
  algorithmId,
  isPublic,
  onPublicChange,
  reset,
}) => {
  const deleteModalRef = React.useRef<HTMLDialogElement | null>(null);
  const { mutate: editAlgorithmMutation, isPending: isPendingEdit } =
    useEditAlgorithm(algorithmId, (data) =>
      onSubmitSuccess(data.algorithm, data.title)
    );
  const { mutate: togglePublicMutation, isPending: isPendingToggle } =
    useEditAlgorithm(algorithmId, (data) => onPublicChange(data.is_public));
  const { mutate: deleteAlgorithmMutation, isPending: isPendingDelete } =
    useDeleteAlgorithm(algorithmId, () => onDeleteSuccess());

  const showDeleteModal = async () => {
    deleteModalRef.current?.showModal();
  };

  const publicUrl = `${window.location.origin}/form/public/${algorithmId}?demo=true`;

  const copyPublicUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    enqueueSnackbar("Public URL copied to clipboard", { variant: "success" });
  };

  return (
    <>
      <div className="flex flex-col items-center gap-4 w-full">
        <div className="flex items-center gap-4">
          <ButtonLoadingOnBusy
            className="btn mr-4"
            disabled={!isEdited}
            onClick={() => editAlgorithmMutation(algorithmData)}
            busy={isPendingEdit}
          >
            Save Edits
          </ButtonLoadingOnBusy>
          <button className="btn mr-4" disabled={!isEdited} onClick={reset}>
            Reset Changes
          </button>
          <button className="btn" onClick={showDeleteModal}>
            <TrashIcon className="h-4 w-4" />
          </button>
          <Link to={"/"} className="btn btn-link">
            Home
          </Link>
          <Link to={`/form/${algorithmId}`} className="btn btn-link">
            View in Interactive Form
          </Link>
        </div>
        <div className="flex items-center gap-3 p-3 border border-base-300 rounded-lg">
          <span className="text-sm font-medium">Public Algorithm:</span>
          <input
            type="checkbox"
            className="toggle toggle-primary"
            checked={isPublic}
            disabled={isPendingToggle}
            onChange={(e) =>
              togglePublicMutation({ is_public: e.target.checked })
            }
          />
          {isPublic && (
            <div className="flex items-center gap-2 ml-4">
              <input
                type="text"
                readOnly
                value={publicUrl}
                className="input input-bordered input-sm w-80 text-xs"
              />
              <button
                className="btn btn-sm btn-outline"
                onClick={copyPublicUrl}
              >
                Copy
              </button>
            </div>
          )}
        </div>
      </div>
      <DeleteFormModal
        ref={deleteModalRef}
        content={algorithmData.algorithm.question}
        handleDelete={() => deleteAlgorithmMutation()}
        isDeleting={isPendingDelete}
      />
    </>
  );
};

const AlgorithmBuilderCreateButtons: React.FC<{
  algorithmData: IFormAlgorithm;
  onSubmitSuccess: (
    uuid: string,
    submittedForm: FormStep,
    submittedTitle: string
  ) => void;
  reset: () => void;
}> = ({ algorithmData, onSubmitSuccess, reset }) => {
  const {
    mutate: createAlgorithmMutation,
    isPending,
    isSuccess,
  } = useCreateAlgorithm((data) =>
    onSubmitSuccess(data.uuid, data.algorithm, data.title)
  );
  return (
    <>
      <ButtonLoadingOnBusy
        className="btn mr-4"
        onClick={() => createAlgorithmMutation(algorithmData)}
        busy={isPending || isSuccess}
      >
        Submit Form
      </ButtonLoadingOnBusy>
      <button className="btn" onClick={reset}>
        Reset to default
      </button>
      <Link to={"/"} className="btn btn-link">
        Home
      </Link>
    </>
  );
};

const AlgorithmBuilderScreen: React.FC<{
  formData: FormStep;
  onChange: (formData: FormStep) => void;
  undo: () => void;
  redo: () => void;
  hasUndo: boolean;
  hasRedo: boolean;
}> = ({ formData, onChange, undo, redo, hasUndo, hasRedo }) => {
  const inputRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    inputRef.current?.scrollIntoView({ inline: "center", block: "center" });
  }, []);

  return (
    <div className="relative mb-2 border-dashed border border-secondary">
      <div
        className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,#fff,rgba(255,255,255,0.6))] dark:bg-grid-slate-700/25 dark:[mask-image:linear-gradient(0deg,rgba(255,255,255,0.1),rgba(255,255,255,0.5))]"
        style={{
          zIndex: -1,
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32' width='32' height='32' fill='none' stroke='rgb(51 65 85 / 0.25)'%3e%3cpath d='M0 .5H31.5V32'/%3e%3c/svg%3e")`,
          backgroundPosition: "10px 10px",
        }}
      ></div>
      <div className="flex flex-nowrap overflow-auto pt-2">
        <ArcherContainer
          strokeColor="#7c82a8"
          className="m-auto h-[calc(77vh)]"
          noCurves={true}
        >
          <FormStepComponent
            formData={formData}
            onChange={onChange}
            id={["formRoot"]}
            ref={inputRef}
          />
        </ArcherContainer>
      </div>
      <div className="absolute top-0 left-0">
        <button className="btn m-3 mr-0" disabled={!hasUndo} onClick={undo}>
          <ArrowUturnLeftIcon className="h-4 w-4" />
        </button>
        <button className="btn m-3 mr-0" disabled={!hasRedo} onClick={redo}>
          <ArrowUturnRightIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="absolute invisible sm:visible bottom-0 left-0 ml-3 mb-3 flex flex-col opacity-35 hover:opacity-65">
        <div>
          (<kbd className="kbd kbd-xs">Ctrl</kbd>/
          <kbd className="kbd kbd-xs">⌘</kbd>)+
          <kbd className="kbd kbd-xs">Enter</kbd>
          <span className="text-sm">: Commit text edit</span>
        </div>
        <div>
          <kbd className="kbd kbd-xs">Esc</kbd>
          <span className="text-sm">: Cancel text edit</span>
        </div>
      </div>
    </div>
  );
};

export default AlgorithmBuilder;
